import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { User } from '@/types';
import { formatFullName } from '@/lib/format';
import { Table, Column, SortDirection } from '@/components/ui/Table';
import { Badge, getRoleBadgeVariant, getStatusBadgeVariant } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { formatDate } from '@/lib/utils';

export interface UserTableProps {
  users: User[];
  isLoading?: boolean;
  onEdit: (user: User) => void;
  onDeactivate: (user: User) => void;
  onActivate: (user: User) => void;
  onDelete: (user: User) => void;
  onResetPassword: (user: User) => void;
  /** When false, hide Video/Audio columns and consent shortcut. Defaults to true. */
  responsesEnabled?: boolean;
}

/** Value shown in the cell, so header clicks reorder what the user sees. */
function sortValue(user: User, column: string): string | number {
  switch (column) {
    case 'name':
      return (formatFullName(user.firstName, user.lastName) || user.email).toLocaleLowerCase();
    case 'role':
      return user.role;
    case 'isActive':
      return user.isActive ? 'active' : 'inactive';
    case 'canRespondVideo':
      return responseSortLabel(user, user.canRespondVideo);
    case 'canRespondAudio':
      return responseSortLabel(user, user.canRespondAudio);
    case 'createdAt':
      return Date.parse(user.createdAt) || 0;
    default:
      return '';
  }
}

function responseSortLabel(user: User, allowed?: boolean): string {
  if (user.role === 'congregant') return '';
  return allowed ? 'yes' : 'no';
}

function compareUsers(a: User, b: User, column: string): number {
  const left = sortValue(a, column);
  const right = sortValue(b, column);
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }
  return String(left).localeCompare(String(right), undefined, { sensitivity: 'base' });
}

export function UserTable({
  users,
  isLoading = false,
  onEdit,
  onDeactivate,
  onActivate,
  onDelete,
  onResetPassword,
  responsesEnabled = true,
}: UserTableProps) {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const sortedUsers = useMemo(() => {
    if (!sortColumn || !sortDirection) return users;
    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...users].sort((a, b) => compareUsers(a, b, sortColumn) * direction);
  }, [users, sortColumn, sortDirection]);

  const handleSort = (column: string, direction: SortDirection) => {
    setSortColumn(direction ? column : null);
    setSortDirection(direction);
  };

  const columns: Column<User>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (user) => (
        <div className="flex flex-col">
          <span className="font-medium text-slate-900 dark:text-white">
            {formatFullName(user.firstName, user.lastName) || 'Unnamed User'}
          </span>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {user.email}
          </span>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      sortable: true,
      render: (user) => (
        <Badge variant={getRoleBadgeVariant(user.role)}>
          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'isActive',
      header: 'Status',
      sortable: true,
      render: (user) => (
        <Badge variant={getStatusBadgeVariant(user.isActive ? 'active' : 'inactive')}>
          {user.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    ...(responsesEnabled
      ? ([
          {
            key: 'canRespondVideo',
            header: 'Video',
            sortable: true,
            render: (user: User) => (
              <span className="text-slate-600 dark:text-slate-400 text-sm">
                {user.role === 'congregant'
                  ? '—'
                  : user.canRespondVideo ?? false
                    ? 'Yes'
                    : 'No'}
              </span>
            ),
          },
          {
            key: 'canRespondAudio',
            header: 'Audio',
            sortable: true,
            render: (user: User) => (
              <span className="text-slate-600 dark:text-slate-400 text-sm">
                {user.role === 'congregant'
                  ? '—'
                  : user.canRespondAudio ?? false
                    ? 'Yes'
                    : 'No'}
              </span>
            ),
          },
        ] as Column<User>[])
      : []),
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (user) => (
        <span className="text-slate-600 dark:text-slate-400">
          {formatDate(user.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '200px',
      render: (user) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(user)}
            className="text-primary-600 hover:text-primary-700"
          >
            <EditIcon className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onResetPassword(user)}
            className="text-slate-600 hover:text-slate-700"
            title="Reset Password"
          >
            <KeyIcon className="w-4 h-4" />
          </Button>
          {responsesEnabled && (user.canRespondVideo || user.canRespondAudio) && (
            <Link
              to={`/admin/settings?tab=responses&user_id=${user.id}`}
              className="inline-flex items-center justify-center rounded-md p-2 text-slate-600 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
              title="View recording consents"
            >
              <DocumentIcon className="w-4 h-4" />
            </Link>
          )}
          {user.isActive ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDeactivate(user)}
              className="text-red-600 hover:text-red-700"
              title="Deactivate"
            >
              <BanIcon className="w-4 h-4" />
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onActivate(user)}
                className="text-green-600 hover:text-green-700"
                title="Activate"
              >
                <CheckIcon className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(user)}
                className="text-red-600 hover:text-red-700"
                title="Delete permanently"
              >
                <TrashIcon className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
      <Table
        columns={columns}
        data={sortedUsers}
        keyExtractor={(user) => user.id}
        isLoading={isLoading}
        emptyMessage="No users found"
        sortColumn={sortColumn ?? undefined}
        sortDirection={sortDirection}
        onSort={handleSort}
      />
    </div>
  );
}

// Icons
function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  );
}

function KeyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
      />
    </svg>
  );
}

function BanIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}
